import type { SettingsSourceCard, SettingsContext, SettingsSource } from '../models/settings-hub.model';

import { action, deviceToggle, field } from './settings-builders';
import { autoPrintDocumentOptions, documentPrinterOptions, LEAN_PMS_PAYMENT_TYPE, nonLeanPaymentMethodOptions, pienissimoGiftcardMethodOptions, qrCodeSizeOptions } from './settings-options';
import { URL_PATTERN } from './settings-patterns';
import { inCountry, notInCountry } from './settings-rules';

/** Payment method types that make the PMS settings available, as the legacy page did */
const ZUCCHETTI_PMS_PAYMENT_TYPES = [22, 23, 36, 43];

const hasPaymentMethodOfType = ({ data }: SettingsContext, types: number[]) =>
    data.paymentMethods.some(paymentMethod => types.includes(paymentMethod.payment_method_type_id!));


const UVE_FIELDS = ['uve.user', 'uve.password', 'uve.appClientId', 'uve.url', 'uve.idCompany', 'uve.idPagamento', 'uve.area', 'uve.lotto'] as const;
const uveEnabled = ({ values }: SettingsContext) => values['uve.enabled'] === true;
const digitalHubEnabled = ({ values }: SettingsContext) => values['daily_closing.digitalhub.enable'] === true;
const digitalHubDisabled = (context: SettingsContext) => !digitalHubEnabled(context);
const simplePaymentMapping = ({ values }: SettingsContext) => !values['lean_pms.advanced_payment_mapping_enable'];
/** Seconds between the checks of the sales to print automatically, when the preference is not set */
const AUTO_PRINT_DEFAULT_INTERVAL = 10;

const autoPrintEnabled = ({ values }: SettingsContext) => values['general.auto_print.enable'] === true;
const hasSavedPienissimoToken = ({ getSavedPreference }: SettingsContext) => !!getSavedPreference('pienissimo.token');

const HOSPITALITY_CARDS: SettingsSourceCard[] = [
    {
        id: 'pms-zucchetti',
        visibleWhen: context => hasPaymentMethodOfType(context, ZUCCHETTI_PMS_PAYMENT_TYPES),
        settings: [
            field.toggle('zucchetti_pms.send_other_sales', 'SETTINGS.ZUCCHETTI_PMS.SEND_OTHER_SALES')
        ]
    },
    {
        id: 'pms-lean',
        visibleWhen: context => hasPaymentMethodOfType(context, [LEAN_PMS_PAYMENT_TYPE]),
        settings: [
            field.toggle('lean_pms.hide_room_prefix', 'SETTINGS.LEAN_PMS.HIDE_ROOM_PREFIX'),
            field.toggle('lean_pms.group_charges_by_lean_product_code', 'SETTINGS.LEAN_PMS.GROUP_CHARGES_BY_LEAN_PRODUCT_CODE'),
            {
                id: 'lean_pms.cache_expiration_time',
                preferenceKey: 'lean_pms.cache_expiration_time',
                control: 'select',
                numeric: true,
                defaultValue: 30,
                persistDefault: true,
                required: true,
                label: 'SETTINGS.LEAN_PMS.BOOKING_CACHE_DURATION',
                options: () => [1, 10, 20, 30].map(minutes => ({ value: minutes, label: String(minutes) }))
            },
            field.text('lean_pms.notes_category', 'SETTINGS.LEAN_PMS.NOTES_CATEGORY'),
            field.text('lean_pms.sale_id_prefix', 'SETTINGS.LEAN_PMS.SALE_ID_PREFIX'),
            field.toggle('lean_pms.send_prefix_only', 'SETTINGS.LEAN_PMS.SEND_PREFIX_ONLY'),
            field.toggle('lean_pms.enable_daily_closing', 'SETTINGS.LEAN_PMS.ENABLE_DAILY_CLOSING'),
            field.toggle('lean_pms.advanced_payment_mapping_enable', 'SETTINGS.LEAN_PMS.ADVANCED_PAYMENT_MAPPING_ENABLE'),
            field.number('lean_pms.cash_payment_type_id', 'SETTINGS.LEAN_PMS.CASH_PAYMENT_TYPE_ID', { lookup: 'lean-cash-transaction-type', visibleWhen: simplePaymentMapping }),
            field.number('lean_pms.card_payment_type_id', 'SETTINGS.LEAN_PMS.CARD_PAYMENT_TYPE_ID', { lookup: 'lean-cash-transaction-type', visibleWhen: simplePaymentMapping }),
            field.number('lean_pms.cash_difference_payment_type_id', 'SETTINGS.LEAN_PMS.CASH_DIFFERENCE_PAYMENT_TYPE_ID', { lookup: 'lean-cash-transaction-type', visibleWhen: simplePaymentMapping }),
            {
                id: 'lean_pms.advanced_payment_mapping',
                preferenceKey: 'lean_pms.advanced_payment_mapping',
                control: 'object-list',
                label: 'SETTINGS.HUB.FIELDS.LEAN_PAYMENT_MAPPING',
                visibleWhen: ({ values }) => values['lean_pms.advanced_payment_mapping_enable'] === true,
                objectList: {
                    addLabel: 'SETTINGS.HUB.ACTIONS.ADD_PAYMENT_MAPPING',
                    fields: [
                        { key: 'tilby', label: 'SETTINGS.HUB.FIELDS.PAYMENT_METHOD', control: 'select', required: true, grow: 2, options: nonLeanPaymentMethodOptions },
                        { key: 'lean', label: 'SETTINGS.HUB.FIELDS.LEAN_TRANSACTION_TYPE', control: 'number', required: true, lookup: 'lean-cash-transaction-type' }
                    ]
                }
            },
            field.toggle('lean_pms.sync_customers', 'SETTINGS.LEAN_PMS.SYNC_CUSTOMERS'),
            field.toggle('lean_pms.send_extra_sale', 'SETTINGS.LEAN_PMS.SEND_EXTRA_SALE'),
            field.toggle('lean_pms.ask_room_number_on_sale_open', 'SETTINGS.LEAN_PMS.ASK_ROOM_NUMBER_ON_SALE_OPEN'),
            {
                id: 'lean_pms.webhooks.bus',
                preferenceKey: 'lean_pms.webhooks.bus',
                control: 'toggle',
                onValue: 'pre-bus',
                deleteOnEmpty: true,
                label: 'SETTINGS.LEAN_PMS.WEBHOOKS_BUS'
            },
            field.text('lean_pms.webhooks.extra_id', 'SETTINGS.LEAN_PMS.WEBHOOKS_EXTRA_ID'),
            field.number('lean_pms.webhooks.hotel_id', 'SETTINGS.LEAN_PMS.WEBHOOKS_HOTEL_ID', { min: 0 }),
            {
                id: 'reattach-lean-webhook',
                control: 'action',
                actionId: 'reattach-lean-webhook',
                label: 'SETTINGS.LEAN_PMS.REATTACH_WEBHOOK',
                disabledWhen: ({ getSavedPreference }) => !getSavedPreference('lean_pms.webhooks.hotel_id')
            },
            action('disconnect-lean', 'SETTINGS.LEAN_PMS.DISCONNECT_LEAN')
        ]
    },
    {
        id: 'pienissimo',
        settings: [
            {
                id: 'pienissimo.token',
                preferenceKey: 'pienissimo.token',
                control: 'text',
                // As in the legacy page, the error is shown when the token is empty, but the page can be saved anyway
                required: true,
                nonBlocking: true,
                label: 'SETTINGS.SETTINGS.TOKEN_PIENISSIMO',
                // With Pienissimo the sales are always split by items
                onChange: (token, { getSavedPreference }) => ({
                    'cashregister.split_sale.split_items_on_dutch_treat': token ? true : getSavedPreference('cashregister.split_sale.split_items_on_dutch_treat') === true
                })
            },
            field.text('pienissimo.url', 'SETTINGS.SETTINGS.URL_PIENISSIMO'),
            field.text('pienissimo.url_takeaway', 'SETTINGS.SETTINGS.URL_PIENISSIMO_TAKE_AWAY'),
            field.text('pienissimo.url_delivery', 'SETTINGS.SETTINGS.URL_PIENISSIMO_DELIVERY'),
            field.number('pienissimo.days_to_check', 'SETTINGS.SETTINGS.DAYS_TO_CHECK', { min: 2, max: 30 }),
            field.toggle('pienissimo.manage_fidelities', 'SETTINGS.SETTINGS.PIENISSIMO_MANAGE_FIDELITIES'),
            {
                id: 'pienissimo.giftcard_method',
                preferenceKey: 'pienissimo.giftcard_method',
                control: 'select',
                numeric: true,
                label: 'SETTINGS.SETTINGS.PIENISSIMO_GIFTCARD_METHOD_SELECT',
                options: pienissimoGiftcardMethodOptions
            },
            action('activate-pienissimo-sync', 'SETTINGS.SETTINGS.ACTIVATE_PIENISSIMO_SYNC'),
            {
                id: 'deactivate-pienissimo-sync',
                control: 'action',
                actionId: 'deactivate-pienissimo-sync',
                label: 'SETTINGS.SETTINGS.DEACTIVATE_PIENISSIMO_SYNC',
                disabledWhen: context => !hasSavedPienissimoToken(context)
            },
            {
                id: 'sync-pienissimo-products',
                control: 'action',
                actionId: 'sync-pienissimo-products',
                label: 'SETTINGS.SETTINGS.PIENISSIMO_SYNC_PRODUCTS',
                disabledWhen: context => !hasSavedPienissimoToken(context)
            },
            {
                id: 'sync-pienissimo-tables',
                control: 'action',
                actionId: 'sync-pienissimo-tables',
                label: 'SETTINGS.SETTINGS.PIENISSIMO_SYNC_TABLES',
                disabledWhen: context => !hasSavedPienissimoToken(context)
            }
        ]
    },
    {
        id: 'mago',
        visibleWhen: ({ getPreference }) => !!(getPreference('integrations.mago.publish_sales') || getPreference('integrations.mago.publish_customers')),
        settings: [
            action('disconnect-mago-orders', 'SETTINGS.MAGO.ORDERS_LOGOUT')
        ]
    }
];

export const HOSPITALITY_SUBCATEGORY: SettingsSource = {
    id: 'hospitality',
    cards: HOSPITALITY_CARDS
};

export const MARKETING_SUBCATEGORY: SettingsSource = {
    id: 'marketing',
    cards: [
        {
            id: 'four-dem',
            visibleWhen: inCountry('IT'),
            settings: [
                field.toggle('fdem_sync', 'SETTINGS.SETTINGS.ENABLE_SYNC', { onlineFirst: true }),
                {
                    id: 'force-four-dem-sync',
                    control: 'action',
                    actionId: 'force-four-dem-sync',
                    label: 'SETTINGS.SETTINGS.FORCE_SYNC',
                    visibleWhen: ({ values }) => values['fdem_sync'] === true
                },
                field.text('fdem_apikey', 'SETTINGS.SETTINGS.FDEM_API_KEY', { onlineFirst: true, disabledWhen: ({ values }) => !values['fdem_sync'] }),
                field.text('fdem_host', 'SETTINGS.SETTINGS.FDEM_HOST', { disabledWhen: ({ values }) => !values['fdem_sync'] })
            ]
        },
        {
            id: 'self-ordering',
            visibleWhen: inCountry('IT'),
            settings: [
                field.text('orders.strooka_shared_key', 'SETTINGS.SETTINGS.STROOKA_SHARED_KEY'),
                field.text('orders.strooka_header_text', 'SETTINGS.SETTINGS.STROOKA_HEADER_TEXT'),
                field.text('orders.strooka_api_url', 'SETTINGS.SETTINGS.STROOKA_API_URL')
            ]
        }
    ]
};

const RETAIL_CARDS: SettingsSourceCard[] = [
    {
        id: 'uve',
        settings: [
            // With the integration off, the fields are brought back to the values that are saved, as the legacy page does when saving
            field.toggle('uve.enabled', 'SETTINGS.SETTINGS.ENABLE_INTEGRATION_UVE', {
                beforeSave: (enabled, { getSavedPreference }) => enabled === true ? {} : Object.fromEntries(UVE_FIELDS.map(key => [key, getSavedPreference(key)]))
            }),
            field.text('uve.user', 'SETTINGS.SETTINGS.UVE.USER', { required: uveEnabled, visibleWhen: uveEnabled }),
            field.text('uve.password', 'SETTINGS.SETTINGS.UVE.PASSWORD', { required: uveEnabled, visibleWhen: uveEnabled }),
            field.text('uve.appClientId', 'SETTINGS.SETTINGS.UVE.APP_CLIENT_ID', { required: uveEnabled, visibleWhen: uveEnabled }),
            field.text('uve.url', 'SETTINGS.SETTINGS.UVE.URL', { required: uveEnabled, pattern: URL_PATTERN, visibleWhen: uveEnabled }),
            field.text('uve.idCompany', 'SETTINGS.SETTINGS.UVE.ID_COMPANY', { required: uveEnabled, visibleWhen: uveEnabled }),
            field.text('uve.idPagamento', 'SETTINGS.SETTINGS.UVE.ID_PAYMENT', { required: uveEnabled, maxLength: 4, visibleWhen: uveEnabled }),
            field.text('uve.area', 'SETTINGS.SETTINGS.UVE.AREA', { visibleWhen: uveEnabled }),
            field.text('uve.lotto', 'SETTINGS.SETTINGS.UVE.LOT', { visibleWhen: uveEnabled })
        ]
    },
    {
        id: 'digital-hub',
        settings: [
            field.toggle('daily_closing.digitalhub.enable', 'SETTINGS.SETTINGS.DIGITAL_HUB_ENABLE_CORRESPONDING_SUBMISSION'),
            {
                id: 'daily_closing.digitalhub.use_e_invoice_credentials',
                preferenceKey: 'daily_closing.digitalhub.use_e_invoice_credentials',
                control: 'toggle',
                label: 'SETTINGS.SETTINGS.DIGITAL_HUB_E_INVOICE',
                disabledWhen: digitalHubDisabled
            },
            {
                id: 'open-digitalhub-credentials',
                control: 'action',
                actionId: 'open-digitalhub-credentials',
                label: 'SETTINGS.SETTINGS.DIGITAL_HUB_CREDENTIALS_FOR_CLOSING_SUBMISSION',
                disabledWhen: context => digitalHubDisabled(context) || context.values['daily_closing.digitalhub.use_e_invoice_credentials'] === true
            },
            field.text('daily_closing.digitalhub.from_sw', 'SETTINGS.SETTINGS.DIGITAL_HUB_FROMSW', { disabledWhen: digitalHubDisabled }),
            field.text('daily_closing.digitalhub.to_sw', 'SETTINGS.SETTINGS.DIGITAL_HUB_TOSW', { disabledWhen: digitalHubDisabled }),
            field.toggle('daily_closing.digitalhub.enable_riepilogo_zcoge_categories', 'SETTINGS.SETTINGS.DIGITAL_HUB_ZCOGE', { disabledWhen: digitalHubDisabled }),
            field.toggle('daily_closing.digitalhub.enable_sedepdv', 'SETTINGS.SETTINGS.DIGITAL_HUB_SEDEPDV', { disabledWhen: digitalHubDisabled }),
            {
                id: 'daily_closing.digitalhub.sedepdv',
                preferenceKey: 'daily_closing.digitalhub.sedepdv',
                control: 'text',
                label: 'SETTINGS.SETTINGS.DIGITAL_HUB_SEDEPDV',
                visibleWhen: context => digitalHubEnabled(context) && context.values['daily_closing.digitalhub.enable_sedepdv'] === true
            }
        ]
    },
    {
        id: 'retailforce',
        visibleWhen: notInCountry('IT'),
        settings: [
            field.text('retailforce.identification', 'SETTINGS.RETAILFORCE.IDENTIFICATION'),
            field.text('retailforce.store_number', 'SETTINGS.RETAILFORCE.STORE_NUMBER'),
            field.text('retailforce.cloud_api_key', 'SETTINGS.RETAILFORCE.CLOUD_API_KEY'),
            field.text('retailforce.cloud_api_secret', 'SETTINGS.RETAILFORCE.CLOUD_API_SECRET'),
            field.select('retailforce.qr_code_size', 'SETTINGS.RETAILFORCE.QR_CODE_SIZE', { numeric: true, defaultValue: 6, options: qrCodeSizeOptions }),
            field.toggle('retailforce.local', 'SETTINGS.RETAILFORCE.LOCAL'),
            {
                id: 'init-retailforce-auto-close',
                control: 'action',
                actionId: 'init-retailforce-auto-close',
                label: 'SETTINGS.RETAILFORCE.INIT_AUTO_CLOSE',
                visibleWhen: context => inCountry('FR')(context) && !context.values['retailforce.local']
            }
        ]
    }
];

export const RETAIL_SUBCATEGORY: SettingsSource = {
    id: 'retail',
    cards: RETAIL_CARDS
};

export const AUTOMATIONS_SUBCATEGORY: SettingsSource = {
    id: 'automations',
    cards: [
        {
            id: 'auto-print',
            settings: [
                field.toggle('general.auto_print.enable', 'SETTINGS.SETTINGS.ENABLE_AUTOMATIC_PRINT', { id: 'automations.auto_print.enable' }),
                deviceToggle('print-external-orders', 'SETTINGS.SETTINGS.ENABLE_COMMAND_PRINT', { description: 'SETTINGS.HUB.DEVICE_ONLY', visibleWhen: autoPrintEnabled }),
                deviceToggle('print-external-sales', 'SETTINGS.SETTINGS.ENABLE_BILL_PRINT', { description: 'SETTINGS.HUB.DEVICE_ONLY', visibleWhen: autoPrintEnabled }),
                // Printer and document of the sales printed automatically: the document depends on the printer, and one needs the other
                field.select('cashregister.batch_sale_print.default_printer_id', 'SETTINGS.SETTINGS.DEFAULT_PRINTER', {
                    numeric: true,
                    options: documentPrinterOptions,
                    deleteOnEmpty: true,
                    resetOnChange: ['cashregister.batch_sale_print.default_document_id'],
                    required: ({ values }) => !!values['cashregister.batch_sale_print.default_document_id'],
                    visibleWhen: autoPrintEnabled
                }),
                field.select('cashregister.batch_sale_print.default_document_id', 'SETTINGS.SETTINGS.DEFAULT_DOCUMENT', {
                    options: autoPrintDocumentOptions,
                    optionsDependOn: ['cashregister.batch_sale_print.default_printer_id'],
                    deleteOnEmpty: true,
                    required: ({ values }) => !!values['cashregister.batch_sale_print.default_printer_id'],
                    visibleWhen: autoPrintEnabled
                }),
                field.number('cashregister.batch_sale_print.check_interval', 'SETTINGS.HUB.FIELDS.AUTO_PRINT_INTERVAL', {
                    defaultValue: AUTO_PRINT_DEFAULT_INTERVAL,
                    suffix: 's',
                    deleteOnEmpty: true,
                    visibleWhen: autoPrintEnabled
                })
            ]
        }
    ]
};
